<article data-importWorksheetDialogComponent>
    <h1 mat-dialog-title class="h1 h1--component-title">
        {{ data.title | translate | async }}
    </h1>

    <!-- Instructions -->
    <mat-horizontal-stepper linear #stepper (selectionChange)="onStepChange($event)" (animationDone)="applyAODAFixes()">
        <!-- Customize step icon -->
        <ng-template matStepperIcon="edit" let-index="index">
            <span class="font-weight--medium" (click)="goto(index)">{{ index + 1 }}</span>
        </ng-template>
        <ng-template matStepperIcon="done" let-index="index">
            <span class="font-weight--medium" (click)="goto(index)">{{ index + 1 }}</span>
        </ng-template>
        <ng-template matStepperIcon="number" let-index="index">
            <span class="font-weight--medium" (click)="goto(index)">{{ index + 1 }}</span>
        </ng-template>

        <mat-step>
            <ng-template matStepLabel>
                <p class="font-weight--medium" (click)="goto(0)">{{ "calculator.import.instructions" | translate | async }}</p>
            </ng-template>
            <p>{{ "calculator.import.instructions.desc" | translate | async }}</p>
            <ul id="importInstructions">
                <li>{{ "calculator.import.instructions.desc.1" | translate | async }}</li>
                <li>{{ "calculator.import.instructions.desc.2" | translate | async }}</li>
                <li>{{ "calculator.import.instructions.desc.3" | translate | async }}</li>
            </ul>

            <div>
                <button mat-button class="button button--red-secondary" [mat-dialog-close]="false">
                    {{ "dialog.action.cancel" | translate | async }}
                </button>
                <button mat-button matStepperNext class="button button--green-primary">
                    {{ "calculator.import.button.next.step" | translate | async }}
                </button>
            </div>
        </mat-step>

        <!-- File upload -->
        <mat-step [editable]="editable" [stepControl]="fbUpload">
            <div [formGroup]="fbUpload">
                <ng-template matStepLabel>
                    <span class="font-weight--medium">{{ "calculator.import.select.upload" | translate | async }}</span>
                </ng-template>
                <p>{{ "calculator.import.select.upload.desc" | translate | async }}</p>
                <div class="flex flex--cols-2">
                    <mat-form-field appearance="outline" class="flex-child mt-1 mb-1" color="accent">
                        <mat-label>{{ "calculator.import.select.upload.file.to.upload" | translate | async }}</mat-label>
                        <input matInput readonly formControlName="fileName" (click)="browseFile()" id="fileName" minlength="5" maxlength="255" [errorStateMatcher]="errorStateMatcher" />
                        @if (fbUpload.errors?.required) {
                        <mat-error>
                            {{ "calculator.import.error.file.required" | translate | async }}
                        </mat-error>
                        } @if (fbUpload.errors?.fileType) {
                        <mat-error>
                            {{ "calculator.import.error.file.type" | translate | async }}
                        </mat-error>
                        } @if (fbUpload.errors?.fileFormat) {
                        <mat-error innerHTML="{{ 'calculator.import.error.file.format' | translate | async }}">
                            {{ "calculator.import.error.file.format" | translate | async }}
                        </mat-error>
                        } @if (fbUpload.errors?.maxSize) {
                        <mat-error>
                            {{ "calculator.import.error.file.size" | translate : [10] | async }}
                        </mat-error>
                        }
                    </mat-form-field>

                    <button mat-raised-button (click)="browseFile()" class="button button--black-secondary">
                        {{ "calculator.import.select.upload.button.select.file" | translate | async }}
                    </button>

                    <label for="fileUpload" class="visually-hidden">
                        {{ "calculator.import.select.upload.button.select.file" | translate | async }}
                    </label>
                    <input type="file" id="fileUpload" #fileUpload formControlName="file" [accept]="acceptedFileExt" (change)="onFileSelected($event)" class="input_fileupload--hidden" />
                </div>

                <div>
                    <button mat-button class="button button--red-secondary" [mat-dialog-close]="false">
                        {{ "dialog.action.cancel" | translate | async }}
                    </button>
                    <button mat-button (click)="upload().subscribe()" class="button button--green-primary">
                        {{ "calculator.import.button.next.step" | translate | async }}
                    </button>
                </div>
            </div>
        </mat-step>

        <!-- Edit details - Select calculator(s) -->
        <mat-step [editable]="editable" [stepControl]="fbWorksheets">
            <div [formGroup]="fbWorksheets" id="importDetails">
                <ng-template matStepLabel>
                    <span class="font-weight--medium">{{ "calculator.import.edit.details" | translate | async }}</span>
                </ng-template>
                @if (!worksheetsImport?.error) {
                <h5 class="mt-0">{{ "calculator.import.edit.details.found.calculator" | translate : [$safeNavigationMigration(allWorksheets?.length)] | async }}</h5>
                <p>{{ "calculator.import.edit.details.select.calculator" | translate | async }}</p>
                <div data-worksheetList class="mb-1">
                    @for (worksheet of allWorksheets; track worksheet; let i = $index) {
                    <div class="flex flex--cols-3 {{ isInvalidWorksheet(worksheet) ? 'align-baseline' : 'align-end' }}">
                        @if (allWorksheets.length > 1) {
                        <div class="flex-child pr-1 ws-checkbox">
                            <mat-checkbox [value]="worksheet.id" [class.disabled]="isInvalidWorksheet(worksheet)" (change)="onCheckboxChange($event)" attr.aria-label="{{ worksheet.name }}">
                            </mat-checkbox>
                        </div>
                        }
                        <div class="flex-child ws-name">
                            @if (!isEditing(i)) {
                            {{ worksheet.name }}
                            @if (!isInvalidWorksheet(worksheet)) {
                            <mat-icon
                                color="accent"
                                tabindex="0"
                                aria-hidden="false"
                                mat-icon-button
                                class="button button--green-tertiary"
                                matTooltip="{{ 'calculator.name.change' | translate | async }}"
                                matTooltipPosition="above"
                                (click)="toggleEditMode($event, i)"
                                (keyup.enter)="toggleEditMode($event, i)"
                                attr.aria-label="{{ 'change.calculator.name' | translate | async }}"
                                >edit</mat-icon
                            >
                            } } @else {
                            <mat-form-field appearance="outline" class="flex-child mb-0" color="accent">
                                <input
                                    matInput
                                    trim="blur"
                                    (blur)="onNameChange($event, i)"
                                    maxlength="30"
                                    autoFocusByContent
                                    formControlName="worksheetName"
                                    attr.aria-label="{{ 'calculator.name.placeholder' | translate | async }}" />
                                @if (worksheetName.errors?.minlength) {
                                <mat-error>
                                    {{ "validation.message.minLength.3" | translate | async }}
                                </mat-error>
                                } @if (worksheetName.errors?.maxlength) {
                                <mat-error>
                                    {{ "validation.message.maxLength.30" | translate | async }}
                                </mat-error>
                                } @if (worksheetName.errors?.empty) {
                                <mat-error>
                                    {{ "validation.message.empty" | translate | async }}
                                </mat-error>
                                }
                            </mat-form-field>
                            }
                        </div>
                        <div class="flex-child flex-child--flex-grow pl-1">
                            ({{ getWorksheetType(worksheet.worksheetTypeId) }}@if (isInvalidWorksheet(worksheet)) {
                            <span> - {{ "calculator.import.error." + getErrorCode(worksheet) | translate | async }}</span>
                            })
                        </div>
                    </div>
                    }
                </div>
                @if (!selectedWorksheetIds?.pristine && selectedWorksheetIds.errors?.required) {
                <div class="mb-1">
                    <mat-error>
                        {{ "calculator.import.error.calculator.required" | translate | async }}
                    </mat-error>
                </div>
                } } @else {
                <div data-worksheetList class="colour--red-primary mb-1" innerHTML="{{ 'calculator.import.error.' + $safeNavigationMigration(worksheetsImport?.error.errorCode) | translate | async }}">
                    {{ "calculator.import.error." + $safeNavigationMigration(worksheetsImport?.error.errorCode) | translate | async }}
                </div>
                }

                <div>
                    <button mat-button class="button button--red-secondary" [mat-dialog-close]="false">
                        {{ "dialog.action.cancel" | translate | async }}
                    </button>
                    <button mat-button (click)="import()" class="button button--green-primary">
                        {{ "calculator.import.button.next.step" | translate | async }}
                    </button>
                </div>
            </div>
        </mat-step>

        <!-- Complete -->
        <mat-step [editable]="editable">
            <ng-template matStepLabel>
                <span class="font-weight--medium">{{ "calculator.import.complete" | translate | async }}</span>
            </ng-template>
            <h5 class="mt-0">{{ "calculator.import.complete.success" | translate : [importedWorksheets.length, selectedWorksheetIds.length] | async }}</h5>
            <p>{{ "calculator.import.complete.success.desc" | translate | async }}</p>
            <div data-importedList class="mb-1">
                @for (worksheet of importedWorksheets; track worksheet) {
                <div class="pb-1">
                    <a role="button" (click)="gotoWorksheet(worksheet)">
                        {{ worksheet.name }}
                    </a>
                    ({{ getWorksheetType(worksheet.worksheetTypeId) }})
                </div>
                }
            </div>
            <div>
                <button mat-button class="button button--red-secondary" [mat-dialog-close]="true">
                    {{ "dialog.action.close" | translate | async }}
                </button>
                <button mat-button matStepperNext (click)="goto(0)" class="button button--green-primary">
                    {{ "calculator.import.button.start.another" | translate | async }}
                </button>
            </div>
        </mat-step>
    </mat-horizontal-stepper>
</article>
